/**
 * Room Members Management (UAT B3/F2, MP-217)
 *
 * Binding of the shared MembersPanel to the rooms.* member procedures,
 * rendered on the room page for managers (room.canManage). Two states:
 *
 * - Inheriting room (`inheritAccess`): there is no room-level roster — the
 *   parent space's gate alone decides — so the panel is replaced by a note
 *   pointing at the space settings.
 * - Explicit ACL: full roster. Unlike the space roster there is no owner row
 *   (rooms have no owner of their own; the space owner and space admins
 *   manage every room implicitly, which the roles-help copy spells out).
 *
 * Effective access is space AND room, so a room member inside a PRIVATE or
 * IP_RESTRICTED space still needs space access — the add-member modal shows
 * a brief hint in that case.
 */

import type { Visibility } from '@polygraf/shared'
import { Info, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { trpc } from '@/trpc/client'
import { MembersPanel } from './members/members-panel'

export interface RoomMembersProps {
	roomId: string
	spaceId: string
	inheritAccess: boolean
	spaceVisibility: Visibility
}

export function RoomMembers({ roomId, spaceId, inheritAccess, spaceVisibility }: RoomMembersProps) {
	// Branch on components, not hooks — inheritAccess can flip live (the room
	// page polls rooms.get), so each branch keeps its own hook order intact.
	if (inheritAccess) {
		return <InheritedAccessNote spaceId={spaceId} />
	}
	return <RoomRoster roomId={roomId} spaceVisibility={spaceVisibility} />
}

/** Inheriting rooms have no roster of their own — say where access lives. */
function InheritedAccessNote({ spaceId }: { spaceId: string }) {
	const { t } = useTranslation()

	return (
		<div className="space-y-4">
			<h2 className="text-lg font-semibold flex items-center gap-2">
				<Users size={20} aria-hidden />
				{t('rooms.members.title')}
			</h2>
			<div className="card-refined p-4 flex items-start gap-3 text-sm text-base-content/70">
				<Info size={16} className="text-primary flex-shrink-0 mt-0.5" aria-hidden />
				<div className="space-y-1">
					<p>{t('rooms.inheritAccess.roomPageNote')}</p>
					<Link
						to={`/spaces/${spaceId}`}
						className="inline-block font-medium text-primary hover:underline"
					>
						{t('rooms.inheritAccess.manageOnSpace')}
					</Link>
				</div>
			</div>
		</div>
	)
}

function RoomRoster({ roomId, spaceVisibility }: { roomId: string; spaceVisibility: Visibility }) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const invalidate = () => utils.rooms.listMembers.invalidate({ roomId })

	const { data: members, isLoading } = trpc.rooms.listMembers.useQuery({ roomId }, { retry: false })

	// addMember doubles as the role-change mutation (server upserts the row).
	const addMemberMutation = trpc.rooms.addMember.useMutation({ onSuccess: invalidate })
	const removeMemberMutation = trpc.rooms.removeMember.useMutation({ onSuccess: invalidate })

	// A room membership only opens the room if the user can also read the
	// parent space (effective access is space AND room) — worth a heads-up
	// while the space itself is member- or network-gated.
	const spaceGateIsRestrictive =
		spaceVisibility === 'PRIVATE' || spaceVisibility === 'IP_RESTRICTED'

	return (
		<MembersPanel
			copyPrefix="rooms.members"
			members={members}
			isLoading={isLoading}
			onAdd={(userId, role) => addMemberMutation.mutateAsync({ roomId, userId, role })}
			onChangeRole={(userId, role) => addMemberMutation.mutateAsync({ roomId, userId, role })}
			onRemove={(userId) => removeMemberMutation.mutateAsync({ roomId, userId })}
			addHint={
				spaceGateIsRestrictive ? (
					<p className="flex items-start gap-1.5 text-xs text-base-content/60">
						<Info size={14} className="flex-shrink-0 mt-0.5 text-primary" aria-hidden />
						{t('rooms.members.spaceAccessHint')}
					</p>
				) : undefined
			}
		/>
	)
}
